import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Assets } from '@nexds/web'
import mapboxgl from 'mapbox-gl'
import { useTheme } from 'styled-components'

import { useMapContext } from '../MapView/MapContext'
import { Container, SLIDER_LENGTH } from './MapChartOpacitySlider.styles'

import { Slider } from '@/presentation/components/Slider'
import { useI18n } from '@/presentation/hooks/useI18n'

// Events the map listens to on its canvas container, which also holds the markers.
// Stopping them at the slider keeps a drag on the track from panning the map, a
// click from reaching the layers underneath (and closing the InfoPanel), the arrow
// keys from moving the map and the wheel from zooming it. It also keeps them from
// the Marker's own mousedown listener, whose preventDefault would stop the range
// input from being dragged at all.
const STOPPED_EVENTS = [
  'pointerdown',
  'mousedown',
  'mouseup',
  'click',
  'dblclick',
  'contextmenu',
  'touchstart',
  'touchend',
  'wheel',
  'keydown',
  'keyup'
]

// Share of the chart edge the slider may cover, so it never runs past the chart
// and stays clear of the close button on the edge's far end.
const MAX_EDGE_SHARE = 0.7

// Below this scale the slider is too small to grab, so it hides instead.
const MIN_SCALE = 0.6

type LngLat = [number, number]

/** A chart edge, from its start corner to its end corner, each as [lng, lat]. */
export type ChartEdge = [LngLat, LngLat]

// Clockwise angle, in degrees, from east to the edge as drawn on a north-up map.
// Web Mercator keeps angles, and its y grows southwards like the screen's.
function edgeRotation([start, end]: ChartEdge): number {
  const from = mapboxgl.MercatorCoordinate.fromLngLat(start)
  const to = mapboxgl.MercatorCoordinate.fromLngLat(end)
  return (Math.atan2(to.y - from.y, to.x - from.x) * 180) / Math.PI
}

// Scale that keeps the slider in proportion to the chart as drawn on screen, or
// null when the chart looks too small for a usable slider.
function sliderScale(map: mapboxgl.Map, [start, end]: ChartEdge): number | null {
  const from = map.project(start)
  const to = map.project(end)
  const scale = Math.min(1, (Math.hypot(to.x - from.x, to.y - from.y) * MAX_EDGE_SHARE) / SLIDER_LENGTH)
  // Rounded so a zoom re-renders the slider only when its size visibly changes.
  return scale < MIN_SCALE ? null : Math.round(scale * 100) / 100
}

interface MapChartOpacitySliderProps {
  /**
   * Chart edge the slider runs along, outside the chart: it starts at the first
   * corner and points to the second. The chart must lie clockwise of the edge, as
   * it does for the top edge of a Mapbox image source (top-left → top-right).
   */
  edge: ChartEdge
  value: number
  onChange: (value: number) => void
  /** Zoom range where the chart layer renders; the slider hides outside it. */
  minZoom?: number
  maxZoom?: number
}

/**
 * Opacity slider pinned to a chart plotted on the map. It is a DOM Marker rather
 * than a symbol layer, so it follows the chart while the map moves and still works
 * as a regular range input.
 */
function MapChartOpacitySlider(props: MapChartOpacitySliderProps) {
  const { edge, value, onChange, minZoom = 0, maxZoom = 24 } = props
  const [[startLng, startLat], [endLng, endLat]] = edge

  const { map } = useMapContext()
  const { t } = useI18n()
  const theme = useTheme()

  // The marker element is a zero-size point on the edge's start corner, so the
  // Marker rotates it (and the slider laid out from it) around that corner. It
  // also gets the Marker's own listeners; the slider renders into a child so its
  // events can be stopped before they bubble up to them.
  const [elements] = useState(() => {
    const marker = document.createElement('div')
    marker.style.width = '0'
    marker.style.height = '0'
    const content = document.createElement('div')
    marker.appendChild(content)
    return { marker, content }
  })
  const markerRef = useRef<mapboxgl.Marker>(null)
  // null while the slider is hidden.
  const [scale, setScale] = useState<number | null>(null)

  useEffect(() => {
    const stop = (event: Event) => event.stopPropagation()
    STOPPED_EVENTS.forEach((type) => elements.content.addEventListener(type, stop))

    return () => {
      STOPPED_EVENTS.forEach((type) => elements.content.removeEventListener(type, stop))
    }
  }, [elements])

  useEffect(() => {
    if (!map) return

    // `map` alignment keeps the slider on the edge when the map is rotated.
    const marker = new mapboxgl.Marker({ element: elements.marker, anchor: 'top-left', rotationAlignment: 'map' })
      .setLngLat([startLng, startLat])
      .addTo(map)
    markerRef.current = marker

    return () => {
      marker.remove()
      markerRef.current = null
    }
  }, [map, elements])

  useEffect(() => {
    markerRef.current?.setLngLat([startLng, startLat]).setRotation(
      edgeRotation([
        [startLng, startLat],
        [endLng, endLat]
      ])
    )
  }, [map, startLng, startLat, endLng, endLat])

  useEffect(() => {
    if (!map) return

    const edge: ChartEdge = [
      [startLng, startLat],
      [endLng, endLat]
    ]

    // Every frame of a zoom, pan, rotation or tilt can change how big the chart
    // looks. Same zoom bounds as the raster layer: `maxzoom` is exclusive in Mapbox.
    const update = () => {
      const zoom = map.getZoom()
      setScale(zoom >= minZoom && zoom < maxZoom ? sliderScale(map, edge) : null)
    }

    update()
    map.on('move', update)

    return () => {
      map.off('move', update)
    }
  }, [map, minZoom, maxZoom, startLng, startLat, endLng, endLat])

  if (!map || scale === null) return null

  return createPortal(
    <Container style={{ transform: `scale(${scale})` }}>
      <Slider
        ariaLabel={t('MAP_CHART-OPACITY_SLIDER_LABEL')}
        icon={<Assets.Opacity size="sm" color={theme.colors.neutralL5} />}
        value={value}
        onChange={onChange}
      />
    </Container>,
    elements.content
  )
}

export { MapChartOpacitySlider }
